import { memo } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * LiveStatusChip - Indicates an entity is currently live/open.
 * Shows "Live now" when no close time, or "Open until X:XX PM" when one is set.
 *
 * @param {boolean}     isLive    - Whether the entity is currently open
 * @param {string|null} liveUntil - ISO datetime string from API (optional)
 *
 * @idempotent - Same props always produce same output
 * @atomic - Single responsibility: live status badge
 */
const LiveStatusChip = memo(function LiveStatusChip({ isLive, liveUntil }) {
  const { t } = useTranslation()

  if (!isLive) return null

  let untilText = null
  if (liveUntil) {
    const d = new Date(liveUntil)
    if (d > new Date()) {
      const h = d.getHours()
      const m = d.getMinutes()
      const ampm = h >= 12 ? 'PM' : 'AM'
      untilText = `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`
    }
  }

  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
      {untilText
        ? t('checkin.openUntil', { time: untilText, defaultValue: `Open until ${untilText}` })
        : t('checkin.liveNow', 'Live now')}
    </span>
  )
})

export default LiveStatusChip
